import React, { useEffect, useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { KPICard } from '../../components/ui/KPICard';
import { MapPlaceholder } from '../../components/ui/MapPlaceholder';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { FileText, Calendar, AlertTriangle, MapPin, ArrowRight, RefreshCw, Plus, Megaphone, Upload, MoreHorizontal, Filter, Layers, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { FilterChip } from '../../components/ui/FilterChip';
// Mock Data for Chart
const monthlyData = [{
  name: 'Jan',
  reports: 4000,
  issues: 240,
  events: 20
}, {
  name: 'Feb',
  reports: 3000,
  issues: 139,
  events: 15
}, {
  name: 'Mar',
  reports: 2000,
  issues: 980,
  events: 35
}, {
  name: 'Apr',
  reports: 2780,
  issues: 390,
  events: 25
}, {
  name: 'Mei',
  reports: 1890,
  issues: 480,
  events: 40
}, {
  name: 'Jun',
  reports: 2390,
  issues: 380,
  events: 30
}, {
  name: 'Jul',
  reports: 3490,
  issues: 430,
  events: 45
}, {
  name: 'Agu',
  reports: 4200,
  issues: 210,
  events: 50
}, {
  name: 'Sep',
  reports: 3800,
  issues: 320,
  events: 42
}, {
  name: 'Okt',
  reports: 4500,
  issues: 150,
  events: 55
}, {
  name: 'Nov',
  reports: 3900,
  issues: 280,
  events: 48
}, {
  name: 'Des',
  reports: 4800,
  issues: 190,
  events: 60
}];
const activityFeed = [{
  user: 'DLH Kota Bandung',
  action: 'mengirim laporan mingguan',
  time: '10 menit lalu',
  type: 'report',
  detail: 'Laporan volume sampah TPA Sarimukti'
}, {
  user: 'Komunitas Nol Sampah',
  action: 'mendaftar event HPSN',
  time: '25 menit lalu',
  type: 'event',
  detail: 'Aksi bersih sungai Cikapundung'
}, {
  user: 'Admin Pusat',
  action: 'menerbitkan toolkit baru',
  time: '1 jam lalu',
  type: 'content',
  detail: 'Panduan Teknis HPSN 2025'
}, {
  user: 'Warga (via App)',
  action: 'melaporkan tumpukan sampah',
  time: '2 jam lalu',
  type: 'issue',
  detail: 'Lokasi: Pasar Minggu, Jakarta Selatan'
}, {
  user: 'DLH Surabaya',
  action: 'menyelesaikan isu #TKT-8821',
  time: '3 jam lalu',
  type: 'resolve',
  detail: 'Pembersihan TPS liar Kenjeran'
}, {
  user: 'Bank Sampah Induk',
  action: 'update data nasabah',
  time: '4 jam lalu',
  type: 'report',
  detail: '+150 nasabah baru bulan ini'
}, {
  user: 'Sistem',
  action: 'alert anomali data',
  time: '5 jam lalu',
  type: 'issue',
  detail: 'Lonjakan volume sampah Kab. Bogor'
}, {
  user: 'DLH DKI Jakarta',
  action: 'verifikasi komunitas',
  time: '6 jam lalu',
  type: 'community',
  detail: 'Komunitas Ciliwung Bersih (Verified)'
}, {
  user: 'Admin Regional Jabar',
  action: 'login sistem',
  time: '6 jam lalu',
  type: 'system',
  detail: 'IP: 192.168.1.45'
}, {
  user: 'Sistem',
  action: 'generate laporan harian',
  time: '7 jam lalu',
  type: 'system',
  detail: 'Laporan Harian Nasional 20 Feb 2025'
}];
const getActivityDotColor = (type: string) => {
  switch (type) {
    case 'report':
      return 'bg-blue-500';
    case 'issue':
      return 'bg-red-500';
    case 'resolve':
      return 'bg-green-500';
    case 'event':
      return 'bg-purple-500';
    case 'content':
      return 'bg-orange-500';
    case 'community':
      return 'bg-pink-500';
    case 'system':
      return 'bg-gray-500';
    default:
      return 'bg-gray-400';
  }
};
export function DashboardNasional() {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isQuickActionOpen, setIsQuickActionOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState<string | null>(null);
  // Global Filters
  const [timeFilter, setTimeFilter] = useState('30');
  const [programFilter, setProgramFilter] = useState('all');
  const [seriesFilter, setSeriesFilter] = useState('all');
  // Map Drill-down
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastUpdated(new Date());
      setIsRefreshing(false);
    }, 1500);
  };
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleProvinceClick = () => {
    // Simulate clicking a province on the map
    setSelectedProvince('Jawa Barat');
    setIsMapModalOpen(true);
  };
  return <CommandCenterLayout title="Dashboard Nasional" subtitle="Monitoring Real-time Kinerja Pengelolaan Sampah Nasional" data-id="element-831">
      {/* Global Controls Bar */}
      <div className="bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm mb-8 flex flex-wrap items-center gap-3 justify-between" data-id="element-832">
        <div className="flex flex-wrap items-center gap-3" data-id="element-833">
          <div className="flex items-center gap-2 pr-3 border-r border-gray-200" data-id="element-834">
            <Filter className="h-4 w-4 text-gray-500" data-id="element-835" />
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap" data-id="element-836">
              Filter Global:
            </span>
          </div>

          <div className="flex gap-2" data-id="element-837">
            <FilterChip label="7 Hari" active={timeFilter === '7'} onClick={() => setTimeFilter('7')} data-id="element-838" />
            <FilterChip label="30 Hari" active={timeFilter === '30'} onClick={() => setTimeFilter('30')} data-id="element-839" />
            <FilterChip label="Custom" active={timeFilter === 'custom'} onClick={() => setTimeFilter('custom')} data-id="element-840" />
          </div>

          <div className="h-6 w-px bg-gray-200 hidden md:block" data-id="element-841"></div>

          <Select value={programFilter} onChange={e => setProgramFilter(e.target.value)} className="h-9 text-sm w-40" data-id="element-842">
            <option value="all" data-id="element-843">Semua Program</option>
            <option value="pilah" data-id="element-844">Pilah Sampah</option>
            <option value="cegah" data-id="element-845">Cegah Sampah</option>
            <option value="hpsn" data-id="element-846">HPSN</option>
            <option value="sirkular" data-id="element-847">Ekonomi Sirkular</option>
          </Select>

          <Select value={seriesFilter} onChange={e => setSeriesFilter(e.target.value)} className="h-9 text-sm w-40" data-id="element-848">
            <option value="all" data-id="element-849">Semua Series</option>
            <option value="ramadhan" data-id="element-850">Ramadhan Minim Sampah</option>
            <option value="idulfitri" data-id="element-851">Mudik Minim Sampah</option>
            <option value="qurban" data-id="element-852">Qurban Asik Tanpa Plastik</option>
          </Select>
        </div>

        <div className="flex items-center gap-3" data-id="element-853">
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200 whitespace-nowrap" data-id="element-854">
            <span className={`h-2 w-2 rounded-full ${isRefreshing ? 'bg-gray-400' : 'bg-green-500 animate-pulse'}`} data-id="element-855"></span>
            Live: {formatTime(lastUpdated)}
            <button onClick={handleRefresh} className={`ml-1 hover:text-primary-600 transition-colors ${isRefreshing ? 'animate-spin' : ''}`} data-id="element-856">
              <RefreshCw className="h-3 w-3" data-id="element-857" />
            </button>
          </div>

          <Button className="bg-primary-600 hover:bg-primary-700 text-white gap-2 h-9 shadow-sm" onClick={() => setIsQuickActionOpen(true)} data-id="element-858">
            <Plus className="h-4 w-4" data-id="element-859" />
            <span className="hidden sm:inline" data-id="element-860">Quick Action</span>
          </Button>
        </div>
      </div>

      {/* KPI Row - Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-id="element-861">
        <div onClick={() => setSelectedKpi('laporan')} className="cursor-pointer" data-id="element-862">
          <KPICard title="Laporan Valid" value={timeFilter === '7' ? '2,840' : '12,450'} icon={FileText} iconColor="blue" trend={{
          value: timeFilter === '7' ? '5% vs minggu lalu' : '12% vs bulan lalu',
          direction: 'up'
        }} className={selectedKpi === 'laporan' ? 'ring-2 ring-primary-500' : ''} data-id="element-863" />
        </div>
        <div onClick={() => setSelectedKpi('event')} className="cursor-pointer" data-id="element-864">
          <KPICard title="Event Aktif" value={timeFilter === '7' ? '12' : '89'} icon={Calendar} iconColor="purple" trend={{
          value: '5 baru hari ini',
          direction: 'up'
        }} className={selectedKpi === 'event' ? 'ring-2 ring-primary-500' : ''} data-id="element-865" />
        </div>
        <div onClick={() => setSelectedKpi('isu')} className="cursor-pointer" data-id="element-866">
          <KPICard title="Isu Terbuka" value="234" icon={AlertTriangle} iconColor="red" trend={{
          value: '8% vs minggu lalu',
          direction: 'down'
        }} className={selectedKpi === 'isu' ? 'ring-2 ring-primary-500' : ''} data-id="element-867" />
        </div>
        <div onClick={() => setSelectedKpi('daerah')} className="cursor-pointer" data-id="element-868">
          <KPICard title="Partisipasi" value="1.2M" icon={Megaphone} iconColor="green" description="Total interaksi" trend={{
          value: '15% vs bulan lalu',
          direction: 'up'
        }} className={selectedKpi === 'daerah' ? 'ring-2 ring-primary-500' : ''} data-id="element-869" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-870">
        {/* Main Map Area */}
        <div className="lg:col-span-2 space-y-6" data-id="element-871">
          <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-872">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 bg-gray-50/50 border-b border-gray-100 gap-2" data-id="element-873">
              <div data-id="element-874">
                <CardTitle className="text-lg font-semibold text-gray-900" data-id="element-875">
                  Peta Status Kinerja Daerah
                </CardTitle>
                <CardDescription data-id="element-876">
                  Klik provinsi untuk melihat detail kabupaten/kota
                </CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap" data-id="element-877">
                <div className="flex items-center gap-2 text-xs" data-id="element-878">
                  <span className="flex items-center gap-1" data-id="element-879">
                    <span className="w-3 h-3 rounded-full bg-green-500" data-id="element-880"></span>{' '}
                    Baik
                  </span>
                  <span className="flex items-center gap-1" data-id="element-881">
                    <span className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-882"></span>{' '}
                    Perlu Perhatian
                  </span>
                  <span className="flex items-center gap-1" data-id="element-883">
                    <span className="w-3 h-3 rounded-full bg-red-500" data-id="element-884"></span>{' '}
                    Bermasalah
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 relative group" data-id="element-885">
              {/* Overlay for demo interaction */}
              <div className="absolute inset-0 z-10 cursor-pointer opacity-0 hover:opacity-10 transition-opacity bg-blue-500" onClick={handleProvinceClick} title="Klik untuk drill-down ke Provinsi" data-id="element-886"></div>
              <div className="p-4" data-id="element-887">
                <MapPlaceholder data-id="element-888" />
              </div>
              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur p-2 rounded shadow text-xs text-gray-500 z-0 border border-gray-200" data-id="element-889">
                Klik peta untuk simulasi drill-down
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trend Chart */}
          <Card className="shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-890">
            <CardHeader data-id="element-891">
              <CardTitle className="text-lg font-semibold text-gray-900" data-id="element-892">
                Tren Partisipasi & Laporan
              </CardTitle>
              <CardDescription data-id="element-893">
                Statistik laporan masuk, isu dilaporkan, dan event per bulan
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-894">
              <div className="h-[300px] w-full" data-id="element-895">
                <ResponsiveContainer width="100%" height="100%" data-id="element-896">
                  <AreaChart data={monthlyData} margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0
                }} data-id="element-897">
                    <defs data-id="element-898">
                      <linearGradient id="colorReports" x1="0" y1="0" x2="0" y2="1" data-id="element-899">
                        <stop offset="5%" stopColor="#16a34a" stopOpacity={0.8} data-id="element-900" />
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0} data-id="element-901" />
                      </linearGradient>
                      <linearGradient id="colorIssues" x1="0" y1="0" x2="0" y2="1" data-id="element-902">
                        <stop offset="5%" stopColor="#dc2626" stopOpacity={0.8} data-id="element-903" />
                        <stop offset="95%" stopColor="#dc2626" stopOpacity={0} data-id="element-904" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" data-id="element-905" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fill: '#6b7280',
                    fontSize: 12
                  }} data-id="element-906" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                    fill: '#6b7280',
                    fontSize: 12
                  }} data-id="element-907" />
                    <Tooltip contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)'
                  }} data-id="element-908" />
                    <Legend data-id="element-909" />
                    <Area type="monotone" dataKey="reports" name="Laporan Masuk" stroke="#16a34a" fillOpacity={1} fill="url(#colorReports)" data-id="element-910" />
                    <Area type="monotone" dataKey="issues" name="Isu Lingkungan" stroke="#dc2626" fillOpacity={1} fill="url(#colorIssues)" data-id="element-911" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar: Activity Feed & Alerts */}
        <div className="space-y-6" data-id="element-912">
          <Card className="flex flex-col h-[500px] md:h-[600px] shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-913">
            <CardHeader className="pb-3 border-b border-gray-100" data-id="element-914">
              <div className="flex justify-between items-center" data-id="element-915">
                <CardTitle className="text-lg font-semibold text-gray-900" data-id="element-916">
                  Aktivitas Terbaru
                </CardTitle>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600" data-id="element-917">
                  <MoreHorizontal className="h-4 w-4" data-id="element-918" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar" data-id="element-919">
              <div className="divide-y divide-gray-100" data-id="element-920">
                {activityFeed.map((item, i) => <div key={i} className="p-4 hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-921">
                    <div className="flex gap-3" data-id="element-922">
                      <div className="relative" data-id="element-923">
                        <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${getActivityDotColor(item.type)}`} data-id="element-924" />
                        <div className="absolute top-4 left-1 w-px h-full bg-gray-100 -z-10 group-last:hidden" data-id="element-925"></div>
                      </div>
                      <div data-id="element-926">
                        <p className="text-sm text-gray-900" data-id="element-927">
                          <span className="font-medium" data-id="element-928">{item.user}</span>{' '}
                          {item.action}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-929">
                          {item.detail}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1" data-id="element-930">
                          <span className="capitalize bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-medium" data-id="element-931">
                            {item.type}
                          </span>{' '}
                          • {item.time}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </CardContent>
            <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-lg" data-id="element-932">
              <Button variant="ghost" size="sm" className="w-full text-primary-600 hover:text-primary-700 hover:bg-primary-50 font-medium" data-id="element-933">
                Lihat Semua Aktivitas <ArrowRight className="ml-2 h-4 w-4" data-id="element-934" />
              </Button>
            </div>
          </Card>

          <Card className="border-l-4 border-l-yellow-500 shadow-md" data-id="element-935">
            <CardHeader className="pb-2" data-id="element-936">
              <CardTitle className="text-base font-semibold flex items-center text-yellow-800" data-id="element-937">
                <AlertTriangle className="h-4 w-4 mr-2" data-id="element-938" />
                Perlu Perhatian
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-939">
              <div className="space-y-4" data-id="element-940">
                <div className="flex justify-between items-center text-sm p-3 bg-yellow-50 rounded-md border border-yellow-100" data-id="element-941">
                  <span className="text-gray-800 font-medium" data-id="element-942">
                    Laporan tertunda &gt; 3 hari
                  </span>
                  <StatusBadge status="kuning" data-id="element-943">12 Daerah</StatusBadge>
                </div>
                <div className="flex justify-between items-center text-sm p-3 bg-red-50 rounded-md border border-red-100" data-id="element-944">
                  <span className="text-gray-800 font-medium" data-id="element-945">
                    Isu prioritas tinggi
                  </span>
                  <StatusBadge status="merah" data-id="element-946">5 Tiket</StatusBadge>
                </div>

                <div className="pt-2" data-id="element-947">
                  <div className="flex justify-between items-center text-sm mb-1" data-id="element-948">
                    <span className="text-gray-600 font-medium" data-id="element-949">
                      Target partisipasi HPSN
                    </span>
                    <span className="text-gray-900 font-bold" data-id="element-950">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-951">
                    <div className="bg-green-600 h-2 rounded-full transition-all duration-1000" style={{
                    width: '85%'
                  }} data-id="element-952"></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-right" data-id="element-953">
                    Target: 100% (21 Feb)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Action Modal */}
      <Modal isOpen={isQuickActionOpen} onClose={() => setIsQuickActionOpen(false)} title="Quick Actions" description="Akses cepat fitur yang sering digunakan" size="md" data-id="element-954">
        <div className="grid grid-cols-2 gap-4" data-id="element-955">
          <button className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 transition-all group bg-white shadow-sm hover:shadow-md" data-id="element-956">
            <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" data-id="element-957">
              <Megaphone className="h-6 w-6" data-id="element-958" />
            </div>
            <span className="font-medium text-gray-900" data-id="element-959">Buat Event</span>
            <span className="text-xs text-gray-500 mt-1 text-center" data-id="element-960">
              Jadwalkan event nasional baru
            </span>
          </button>

          <button className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-all group bg-white shadow-sm hover:shadow-md" data-id="element-961">
            <div className="h-12 w-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" data-id="element-962">
              <AlertTriangle className="h-6 w-6" data-id="element-963" />
            </div>
            <span className="font-medium text-gray-900" data-id="element-964">Triage Isu</span>
            <span className="text-xs text-gray-500 mt-1 text-center" data-id="element-965">
              Tangani isu prioritas tinggi
            </span>
          </button>

          <button className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-all group bg-white shadow-sm hover:shadow-md" data-id="element-966">
            <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" data-id="element-967">
              <FileText className="h-6 w-6" data-id="element-968" />
            </div>
            <span className="font-medium text-gray-900" data-id="element-969">Publish Konten</span>
            <span className="text-xs text-gray-500 mt-1 text-center" data-id="element-970">
              Upload materi edukasi baru
            </span>
          </button>

          <button className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-200 transition-all group bg-white shadow-sm hover:shadow-md" data-id="element-971">
            <div className="h-12 w-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" data-id="element-972">
              <Upload className="h-6 w-6" data-id="element-973" />
            </div>
            <span className="font-medium text-gray-900" data-id="element-974">Export Data</span>
            <span className="text-xs text-gray-500 mt-1 text-center" data-id="element-975">
              Download laporan format CSV/PDF
            </span>
          </button>
        </div>
      </Modal>

      {/* Map Drill-down Modal */}
      <Modal isOpen={isMapModalOpen} onClose={() => setIsMapModalOpen(false)} title={`Detail Provinsi: ${selectedProvince}`} description="Kinerja pengelolaan sampah per Kabupaten/Kota" size="2xl" data-id="element-976">
        <div className="space-y-6" data-id="element-977">
          <div className="grid grid-cols-3 gap-4" data-id="element-978">
            <div className="p-4 bg-green-50 rounded-lg border border-green-100 text-center" data-id="element-979">
              <p className="text-sm text-gray-500 font-medium" data-id="element-980">
                Rata-rata Skor
              </p>
              <p className="text-3xl font-bold text-green-700 mt-1" data-id="element-981">82.5</p>
              <StatusBadge status="hijau" className="mt-2" data-id="element-982">
                Baik
              </StatusBadge>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200 text-center shadow-sm" data-id="element-983">
              <p className="text-sm text-gray-500 font-medium" data-id="element-984">Total Laporan</p>
              <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-985">1,240</p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200 text-center shadow-sm" data-id="element-986">
              <p className="text-sm text-gray-500 font-medium" data-id="element-987">Isu Terbuka</p>
              <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-988">15</p>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden overflow-x-auto shadow-sm" data-id="element-989">
            <table className="w-full text-sm text-left min-w-[500px]" data-id="element-990">
              <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200" data-id="element-991">
                <tr data-id="element-992">
                  <th className="px-4 py-3" data-id="element-993">Kabupaten/Kota</th>
                  <th className="px-4 py-3 text-center" data-id="element-994">Skor</th>
                  <th className="px-4 py-3 text-center" data-id="element-995">Status</th>
                  <th className="px-4 py-3 text-right" data-id="element-996">Laporan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-997">
                {[{
                name: 'Kota Bandung',
                score: 85,
                status: 'hijau',
                reports: 420
              }, {
                name: 'Kab. Bandung',
                score: 78,
                status: 'hijau',
                reports: 310
              }, {
                name: 'Kota Cimahi',
                score: 72,
                status: 'kuning',
                reports: 150
              }, {
                name: 'Kab. Bandung Barat',
                score: 68,
                status: 'kuning',
                reports: 120
              }, {
                name: 'Kab. Sumedang',
                score: 80,
                status: 'hijau',
                reports: 240
              }].map((row, i) => <tr key={i} className="hover:bg-gray-50 transition-colors" data-id="element-998">
                    <td className="px-4 py-3 font-medium text-gray-900" data-id="element-999">
                      {row.name}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-gray-900" data-id="element-1000">
                      {row.score}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1001">
                      <StatusBadge status={row.status as any} data-id="element-1002">
                        {row.status === 'hijau' ? 'Baik' : 'Perlu Perhatian'}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600" data-id="element-1003">
                      {row.reports}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end" data-id="element-1004">
            <Button variant="secondary" onClick={() => window.location.href = '/command-center/skoring'} data-id="element-1005">
              Lihat Detail Skoring Lengkap
            </Button>
          </div>
        </div>
      </Modal>

      {/* KPI Detail Modal */}
      <Modal isOpen={!!selectedKpi} onClose={() => setSelectedKpi(null)} title={selectedKpi === 'laporan' ? 'Detail Laporan Masuk' : selectedKpi === 'event' ? 'Detail Event Aktif' : selectedKpi === 'isu' ? 'Detail Isu Lingkungan' : 'Detail Partisipasi Publik'} size="lg" footer={<Button onClick={() => setSelectedKpi(null)} data-id="element-1007">Tutup</Button>} data-id="element-1006">
        <div className="space-y-4" data-id="element-1008">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1009">
            <div className="flex-1" data-id="element-1010">
              <p className="text-sm text-gray-500 font-medium" data-id="element-1011">
                Total Periode Ini
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-1012">
                {selectedKpi === 'laporan' ? '12,450' : selectedKpi === 'event' ? '89' : selectedKpi === 'isu' ? '234' : '1.2M'}
              </p>
            </div>
            <div className="flex-1 border-l-0 sm:border-l border-gray-200 pl-0 sm:pl-4 border-t sm:border-t-0 pt-3 sm:pt-0 w-full sm:w-auto" data-id="element-1013">
              <p className="text-sm text-gray-500 font-medium" data-id="element-1014">Pertumbuhan</p>
              <p className="text-lg font-semibold text-green-600 flex items-center mt-1" data-id="element-1015">
                +12.5%{' '}
                <span className="text-xs text-gray-400 ml-1 font-normal" data-id="element-1016">
                  vs bulan lalu
                </span>
              </p>
            </div>
            <div className="flex-1 border-l-0 sm:border-l border-gray-200 pl-0 sm:pl-4 border-t sm:border-t-0 pt-3 sm:pt-0 w-full sm:w-auto" data-id="element-1017">
              <p className="text-sm text-gray-500 font-medium" data-id="element-1018">Target</p>
              <p className="text-lg font-semibold text-gray-900 mt-1" data-id="element-1019">
                92%{' '}
                <span className="text-xs text-gray-400 ml-1 font-normal" data-id="element-1020">
                  tercapai
                </span>
              </p>
            </div>
          </div>

          <h3 className="font-semibold text-gray-900 mt-6 mb-3" data-id="element-1021">
            Breakdown Wilayah
          </h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden overflow-x-auto shadow-sm" data-id="element-1022">
            <table className="w-full text-sm text-left min-w-[400px]" data-id="element-1023">
              <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200" data-id="element-1024">
                <tr data-id="element-1025">
                  <th className="px-4 py-2" data-id="element-1026">Wilayah</th>
                  <th className="px-4 py-2 text-right" data-id="element-1027">Jumlah</th>
                  <th className="px-4 py-2 text-right" data-id="element-1028">Kontribusi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1029">
                {[{
                region: 'Jawa Barat',
                count: 4520,
                percent: '35%'
              }, {
                region: 'DKI Jakarta',
                count: 3210,
                percent: '25%'
              }, {
                region: 'Jawa Timur',
                count: 2840,
                percent: '22%'
              }, {
                region: 'Lainnya',
                count: 1880,
                percent: '18%'
              }].map((row, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-1030">
                    <td className="px-4 py-2 font-medium text-gray-900" data-id="element-1031">
                      {row.region}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-gray-700" data-id="element-1032">
                      {row.count.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500" data-id="element-1033">
                      {row.percent}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </CommandCenterLayout>;
}